import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchToast } from '../context/ToastContext';
import { Comentario, StatusPost, alterarStatusPost as alterarStatusPostSvc, deletarPost as deletarPostSvc, postPorId as fetchPostPorId } from '../services/postService';
// `shareOnFacebook`/`shareOnWhatsApp` NÃO são importados: os botões deles estão
// ocultos (ver o comentário no bloco de compartilhar, abaixo). As funções seguem
// exportadas por `utils/ShareSocials.tsx`, intactas e corretas — restaurar os
// botões é reimportá-las aqui.
import { shareOnX } from '../utils/ShareSocials';
import { CRIAR_CURTIDA, REMOVER_CURTIDA, useCriarCurtida, useRemoverCurtida } from '../services/curtidaService';
import { CRIAR_COMENTARIO, useCriarComentario, deletarComentario as deletarComentarioSvc, useDeletarComentario } from '../services/comentarioService';
import { buildPostPublicUrl } from '../utils/urlBuilder';
import { IdentidadeFeed } from '../types/identidadeFeed';
import {
    escolherIdentidadePadrao,
    identidadeDoPerfilAtivo,
    resolverIdsParaMutacao,
    resolverIdExclusivoParaMutacao,
} from '../utils/identidadesFeed';
import { CurrentProfile } from '../types/profile';
import IdentidadeFeedSelector from './IdentidadeFeedSelector';

interface Midia {
    url: string;
    tipo: string;
    ordem: number;
}

interface PostviewProps {
    user: string;
    time: string;
    des: string;
    avater: string;
    postimage?: string;
    postvideo?: string;
    id: string | number;
    midias?: Midia[];
    curtidasCount?: number;
    jaCurtiu?: boolean;
    comentariosCount?: number;
    comentarios?: Comentario[];
    username: string;
    postAtletaId?: string;
    status?: StatusPost;
    torneioId?: string;
    ligaId?: string;
    identidades?: IdentidadeFeed[];
    /**
     * Perfil ativo (`ProfileContext.currentProfile`), derivado pela page. Define
     * a identidade padrão e, no caminho legado, qual é o ÚNICO autor enviado.
     */
    perfilAtivo?: CurrentProfile | null;
    readOnly?: boolean;
}



const Postview: React.FC<PostviewProps> = ({
    user,
    time,
    des,
    avater,
    postimage,
    postvideo,
    id,
    midias,
    curtidasCount,
    jaCurtiu = false,
    comentariosCount,
    comentarios = [],
    username = '',
    postAtletaId,
    status,
    torneioId,
    ligaId,
    identidades,
    perfilAtivo,
    readOnly = false,
}) => {
    const data = new Date(time);
    const formattedTime = data.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }) + ' ' + data.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
    });
    const auth = useAuth();
    const userObj =
        (auth?.user as any) ||
        (typeof window !== 'undefined'
            ? JSON.parse(localStorage.getItem('user') || 'null')
            : null);
    const atletaId = userObj?.atleta?.id ?? '';

    const usarIdentidades = !!identidades && identidades.length > 0;
    const defaultIdentidade = escolherIdentidadePadrao(identidades, perfilAtivo);
    const [identidadeSelecionada, setIdentidadeSelecionada] = useState<IdentidadeFeed | null>(defaultIdentidade);

    // Caminho legado (sem `identidades`): resolve UMA identidade por prioridade —
    // perfil ativo, senão liga, senão torneio, senão o atleta logado. Antes daqui
    // saía `autorAtletaId` E `autorLigaId` no mesmo payload, o backend gravava os
    // dois e a renderização preferia o atleta: era o mecanismo do defeito C-01.
    const identidadeLegada: IdentidadeFeed | null =
        identidadeDoPerfilAtivo(perfilAtivo) ||
        (ligaId ? { tipo: 'LIGA', id: ligaId, nome: '' } : null) ||
        (torneioId ? { tipo: 'TORNEIO', id: torneioId, nome: '' } : null) ||
        (atletaId ? { tipo: 'ATLETA', id: atletaId, nome: '' } : null);

    const identidadeParaMutacao = usarIdentidades ? identidadeSelecionada : identidadeLegada;

    const ids = identidadeParaMutacao
        ? resolverIdsParaMutacao(identidadeParaMutacao)
        : { autorAtletaId: '', autorTorneioId: '', autorLigaId: '' };
    const idsExclusivos = identidadeParaMutacao
        ? resolverIdExclusivoParaMutacao(identidadeParaMutacao)
        : { autorAtletaId: undefined, autorTorneioId: undefined, autorLigaId: undefined };
    const canInteract = !!identidadeParaMutacao;

    const [mutCriarCurtida, { loading: liking }] = useCriarCurtida();
    const [mutRemoverCurtida, { loading: unliking }] = useRemoverCurtida();

    const [isOpen, setIsOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [postOptionsOpen, setPostOptionsOpen] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<StatusPost | undefined>(undefined);
    const isAdmin = userObj?.role === 'ADMIN';
    React.useEffect(() => { setCurrentStatus(status); }, [status]);
    const [isActive, setIsActive] = useState(jaCurtiu);
    const [mediaErrors, setMediaErrors] = useState<number[]>([]);
    const [likes, setLikes] = useState<number>(curtidasCount ?? 0);
    const [commentCount, setCommentCount] = useState<number>(comentariosCount ?? comentarios.length);
    const [commentsList, setCommentsList] = useState<Comentario[]>([...comentarios]);
    const [newComment, setNewComment] = useState('');
    const [showComments, setShowComments] = useState(false);
    const [copied, setCopied] = useState(false);
    const shareMenuRef = React.useRef<HTMLDivElement>(null);
    const shareButtonRef = React.useRef<HTMLDivElement>(null);
    const sharePostRef = React.useRef<HTMLDivElement>(null);
    const [mutCriarComentario, { loading: creatingComment }] = useCriarComentario();
    const [deleting, setDeleting] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [openCommentMenuId, setOpenCommentMenuId] = useState<string | null>(null);
    const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
    const postOptionsTriggerRef = useRef<HTMLElement>(null);
    const postOptionsMenuRef = useRef<HTMLDivElement>(null);
    const commentMenuRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    // Recarrega dados do post ao trocar identidade para atualizar jaCurtido/jaCurtiu
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (!usarIdentidades || !identidadeSelecionada) return;
        const recarregar = async () => {
            try {
                const idsReload = resolverIdExclusivoParaMutacao(identidadeSelecionada);
                const postAtualizado = await fetchPostPorId(
                    String(id),
                    idsReload.autorAtletaId,
                    idsReload.autorTorneioId,
                    idsReload.autorLigaId,
                );
                if (postAtualizado) {
                    setIsActive(postAtualizado.jaCurtido ?? false);
                    setLikes(postAtualizado.curtidasCount ?? 0);
                    setCommentCount(postAtualizado.comentariosCount ?? 0);
                    if (postAtualizado.comentarios) {
                        setCommentsList([...postAtualizado.comentarios]);
                    }
                }
            } catch (err) {
                console.error('Erro ao recarregar post após troca de identidade:', err);
            }
        };
        recarregar();
    }, [identidadeSelecionada]);

    React.useEffect(() => {
        setLikes(curtidasCount ?? 0);
    }, [curtidasCount]);

    const handleMediaError = (index: number) => {
        setMediaErrors((prev) => [...prev, index]);
    };

    const shareLink = buildPostPublicUrl(String(id));

    const toggleOpen = () => {
        const next = !isOpen;
        setIsOpen(next);
        if (next) {
            setPostOptionsOpen(false);
            setOpenCommentMenuId(null);
        }
    };

    // Espelha o listener de clique-fora de `IdentidadeFeedSelector.tsx:18-31`: só
    // registra enquanto o menu está aberto, sempre remove no cleanup. Abrir outro
    // menu (acima/abaixo) fecha este via `toggleOpen`/`togglePostOptions`/
    // `toggleCommentMenu`, então aqui só cuida do clique verdadeiramente fora.
    React.useEffect(() => {
        const handleClickOutsideShare = (e: MouseEvent) => {
            const target = e.target as Node;
            const dentroDoBotao = !!shareButtonRef.current?.contains(target);
            const dentroDoMenu = !!shareMenuRef.current?.contains(target);
            if (!dentroDoBotao && !dentroDoMenu) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutsideShare);
        }
        return () => document.removeEventListener('mousedown', handleClickOutsideShare);
    }, [isOpen]);

    React.useEffect(() => {
        const handleClickOutsidePostOptions = (e: MouseEvent) => {
            const target = e.target as Node;
            const dentroDoBotao = !!postOptionsTriggerRef.current?.contains(target);
            const dentroDoMenu = !!postOptionsMenuRef.current?.contains(target);
            if (!dentroDoBotao && !dentroDoMenu) {
                setPostOptionsOpen(false);
            }
        };
        if (postOptionsOpen) {
            document.addEventListener('mousedown', handleClickOutsidePostOptions);
        }
        return () => document.removeEventListener('mousedown', handleClickOutsidePostOptions);
    }, [postOptionsOpen]);

    React.useEffect(() => {
        const handleClickOutsideCommentMenu = (e: MouseEvent) => {
            if (!openCommentMenuId) return;
            const target = e.target as Node;
            const container = commentMenuRefs.current.get(openCommentMenuId);
            if (container && !container.contains(target)) {
                setOpenCommentMenuId(null);
            }
        };
        if (openCommentMenuId) {
            document.addEventListener('mousedown', handleClickOutsideCommentMenu);
        }
        return () => document.removeEventListener('mousedown', handleClickOutsideCommentMenu);
    }, [openCommentMenuId]);

    React.useEffect(() => {
        if (isOpen && shareMenuRef.current && shareButtonRef.current) {
            const btnRect = shareButtonRef.current.getBoundingClientRect();
            const windowScrollX = window.scrollX || document.documentElement.scrollLeft;
            const menu = shareMenuRef.current;
            const postRef = sharePostRef.current;
            const btnRectPost = postRef?.getBoundingClientRect();
            
            menu.style.position = 'absolute';
            if (btnRectPost) {
                menu.style.top = `${(btnRectPost.height) - 320}px`;
            }
            menu.style.left = `${menu.offsetWidth}px`;

        }
    }, [isOpen]);

    const timeAgo = (dateString: string) => {
        const diff = Date.now() - new Date(dateString).getTime();
        const minutes = Math.floor(diff / 60000);
        // if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `Hoje`;
        const days = Math.floor(hours / 24);
        return `${days}d`;
    };

    const handleCreateComment = async () => {
        if (readOnly) return;
        if (!newComment.trim()) return;
        if (!canInteract) return;
        try {
            const { data } = await mutCriarComentario({
                variables: {
                    input: {
                        texto: newComment,
                        autorAtletaId: ids.autorAtletaId,
                        autorTorneioId: ids.autorTorneioId,
                        autorLigaId: ids.autorLigaId,
                        postId: String(id),
                    },
                },
            });
            const created: Comentario = data.criarComentario;
            setCommentsList((prev) => [...prev, created]);
            setCommentCount((c) => c + 1);
            setNewComment('');
            dispatchToast('Comentário publicado!', 'success');
        } catch (err) {
            dispatchGlobalError('Erro ao criar comentário.');
        }
    };

    const handleLikeComment = async (comentarioId: string) => {
        if (readOnly) return;
        if (!canInteract) return;
        const idx = commentsList.findIndex((c) => c.id === comentarioId);
        if (idx === -1) return;
        const comment = commentsList[idx];
        if (!comment.jaCurtiu) {
            try {
                await mutCriarCurtida({
                    variables: {
                        input: {
                            autorAtletaId: ids.autorAtletaId,
                            autorTorneioId: ids.autorTorneioId,
                            autorLigaId: ids.autorLigaId,
                            comentarioId,
                            postId: '',
                        },
                    },
                });
                const updated = {
                    ...comment,
                    jaCurtiu: true,
                    curtidasCount: (comment.curtidasCount ?? 0) + 1,
                };
                setCommentsList((prev) => prev.map((c, i) => (i === idx ? updated : c)));
                dispatchToast('Curtida registrada!', 'success');
            } catch (err) {
                dispatchGlobalError('Erro ao curtir comentário.');
            }
        } else {
            try {
                await mutRemoverCurtida({
                    variables: {
                        ...idsExclusivos,
                        comentarioId,
                        postId: '',
                    },
                });
                const updated = {
                    ...comment,
                    jaCurtiu: false,
                    curtidasCount: Math.max((comment.curtidasCount ?? 1) - 1, 0),
                };
                setCommentsList((prev) => prev.map((c, i) => (i === idx ? updated : c)));
                dispatchToast('Curtida removida.', 'success');
            } catch (err) {
                dispatchGlobalError('Erro ao remover curtida do comentário.');
            }
        }
    };

    const handleLike = async () => {
        if (readOnly) return;
        if (liking || unliking) return;
        if (!canInteract) return;

        if (!isActive) {
            try {
                await mutCriarCurtida({
                    variables: {
                        input: {
                            autorAtletaId: ids.autorAtletaId,
                            autorTorneioId: ids.autorTorneioId,
                            autorLigaId: ids.autorLigaId,
                            comentarioId: '',
                            postId: String(id),
                        },
                    },
                });
                setLikes((l) => l + 1);
                setIsActive(true);
                dispatchToast('Curtida registrada!', 'success');
            } catch (err) {
                dispatchGlobalError('Erro ao curtir o post.');
            }
        } else {
            try {
                await mutRemoverCurtida({
                    variables: {
                        ...idsExclusivos,
                        comentarioId: '',
                        postId: String(id),
                    },
                });
                setLikes((l) => (l > 0 ? l - 1 : 0));
                setIsActive(false);
                dispatchToast('Curtida removida.', 'success');
            } catch (err) {
                dispatchGlobalError('Erro ao remover curtida do post.');
            }
        }
    };

    const menuClass = isOpen ? ' show' : '';
    const emojiClass = isActive ? ' active' : '';
    const postOptionsMenuClass = postOptionsOpen ? ' show' : '';

    const podeDeletar = !!(
        (postAtletaId && atletaId && postAtletaId === atletaId) ||
        (torneioId && isAdmin)
    );

    const togglePostOptions = () => {
        const next = !postOptionsOpen;
        setPostOptionsOpen(next);
        if (next) {
            setIsOpen(false);
            setOpenCommentMenuId(null);
        }
    };

    const toggleCommentMenu = (comentarioId: string) => {
        const next = openCommentMenuId === comentarioId ? null : comentarioId;
        setOpenCommentMenuId(next);
        if (next) {
            setIsOpen(false);
            setPostOptionsOpen(false);
        }
    };

    const toggleStatusMenu = () => setStatusOpen((v) => !v);

    const handleChangeStatus = async (novo: StatusPost) => {
        try {
            const updated = await alterarStatusPostSvc(String(id), novo);
            setCurrentStatus(updated.status);
            setStatusOpen(false);
            alert('Status atualizado com sucesso.');
        } catch (e) {
            dispatchGlobalError('Erro ao alterar status do post.');
        }
    };

    const handleDeletePost = async () => {
        if (!podeDeletar || deleting) return;
        if (!window.confirm('Tem certeza que deseja deletar este post?')) return;
        try {
            setDeleting(true);
            await deletarPostSvc(String(id));
            setDeleted(true);
        } catch (e) {
            dispatchGlobalError('Erro ao deletar o post.');
        } finally {
            setDeleting(false);
            setPostOptionsOpen(false);
        }
    };

    const handleDeleteComentario = async (comentarioId: string) => {
        if (!window.confirm('Tem certeza que deseja deletar este comentário?')) return;
        try {
            setDeletingMap(prev => ({ ...prev, [comentarioId]: true }));
            await deletarComentarioSvc(comentarioId);
            setCommentsList(prev => prev.filter(cm => cm.id !== comentarioId));
            setCommentCount(c => Math.max(0, c - 1));
        } catch (e) {
            dispatchGlobalError('Erro ao deletar o comentário.');
        } finally {
            setDeletingMap(prev => ({ ...prev, [comentarioId]: false }));
            setOpenCommentMenuId(null);
        }
    };

    const canShowAtivar = currentStatus === 'INATIVO';
    const canShowAprovar = currentStatus === 'REPROVADO' || currentStatus === 'PENDENTE';
    const canShowReprovar = currentStatus === 'ATIVO' || currentStatus === 'APROVADO' || currentStatus === 'PENDENTE';
    const canShowInativar = currentStatus !== 'INATIVO';

    function shareOnTwitter(shareLink: string): React.MouseEventHandler<HTMLSpanElement> | undefined {
        throw new Error('Function not implemented.');
    }

    function shareOnInstagram(shareLink: string): React.MouseEventHandler<HTMLSpanElement> | undefined {
        throw new Error('Function not implemented.');
    }

    if (deleted) return null;

    return (
        <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 mb-3" ref={sharePostRef}>
            <div className="card-body p-0 d-flex">
                <figure className="avatar me-3">
                    <img
                        src={`${avater}`}
                        alt="avatar"
                        className="shadow-sm rounded-circle w45"
                    />
                </figure>
                <h4 className="fw-700 text-grey-900 font-xssss mt-1">
                    {user}<span className='text-primary fw-light ps-2'>{username ? '@'+username : ''}</span>
                    <span className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">{formattedTime}</span>
                </h4>

                {isAdmin && (
                    <div className="ms-auto d-flex align-items-center gap-2">
                        <div id="status-post">
                            <div
                                className={`d-flex align-items-center fw-600 text-grey-900 text-dark lh-26 font-xssss`}
                                onClick={toggleStatusMenu}
                                aria-haspopup="true"
                                aria-expanded={statusOpen}
                            >
                                {currentStatus || 'ATIVO'}
                            </div>
                           
                        </div>
                        {(podeDeletar || isAdmin) && (
                            <div className="pointer position-relative" id="post-options">
                                <i
                                    ref={postOptionsTriggerRef}
                                    className="bi bi-three-dots text-grey-900 btn-round-md font-xss pointer"
                                    onClick={togglePostOptions}
                                    aria-expanded={postOptionsOpen}
                                aria-haspopup="true"
                                ></i>
                                <div
                                    ref={postOptionsMenuRef}
                                    className={`dropdown-menu dropdown-menu-end p-2 rounded-xxl border-0 shadow-lg ${postOptionsMenuClass}`}
                                    style={{ minWidth: 160 }}
                                >
                                    {canShowAtivar && (
                                        <button className="dropdown-item" onClick={() => handleChangeStatus('ATIVO')}>Ativar</button>
                                    )}
                                    {canShowAprovar && (
                                        <button className="dropdown-item" onClick={() => handleChangeStatus('APROVADO')}>Aprovar</button>
                                    )}
                                    {canShowReprovar && (
                                        <button className="dropdown-item" onClick={() => handleChangeStatus('REPROVADO')}>Reprovar</button>
                                    )}
                                    {canShowInativar && (
                                        <button className="dropdown-item" onClick={() => handleChangeStatus('INATIVO')}>Inativar</button>
                                    )}
                                    {podeDeletar && (
                                        <button
                                            className="dropdown-item text-danger"
                                            onClick={handleDeletePost}
                                            disabled={deleting}
                                        >
                                            {deleting ? 'Deletando...' : 'Deletar Post'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!isAdmin && podeDeletar && (
                    <div className="ms-auto d-flex align-items-center gap-2">
                        <div className="pointer position-relative" id="post-options">
                            <i
                                ref={postOptionsTriggerRef}
                                className="bi bi-three-dots text-grey-900 btn-round-md font-xss"
                                onClick={togglePostOptions}
                                aria-expanded={postOptionsOpen}
                                aria-haspopup="true"
                            ></i>
                            <div
                                ref={postOptionsMenuRef}
                                className={`dropdown-menu dropdown-menu-end p-2 rounded-xxl border-0 shadow-lg ${postOptionsMenuClass}`}
                                style={{ minWidth: 160 }}
                            >
                                <button
                                    className="dropdown-item text-danger"
                                    onClick={handleDeletePost}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Deletando...' : 'Deletar Post'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {postvideo && (
                <div className="card-body p-0 mb-3 rounded-3 overflow-hidden uttam-die">
                    <a href="/defaultvideo" className="video-btn">
                        <video autoPlay loop className="float-right w-100">
                            <source src={`/assets/images/${postvideo}`} type="video/mp4" />
                        </video>
                    </a>
                </div>
            )}

            <div className="card-body p-0 me-lg-5">
                <p className="fw-500 text-grey-500 lh-26 font-xssss w-100 mb-2">
                    {des}
                </p>
            </div>


            {midias && midias.length > 0
                ? midias
                      .slice()
                      .sort((a, b) => a.ordem - b.ordem)
                      .map((midia, index) => (
                          <div key={index} className="card-body d-block p-0 mb-3">
                              <div className="row ps-2 pe-2">
                                  <div className="col-sm-12 p-1">
                                      {midia.tipo === 'IMAGEM' && !mediaErrors.includes(index) && (
                                          <img
                                              src={midia.url}
                                              alt="midia"
                                              className="rounded-3 w-100"
                                              onError={() => handleMediaError(index)}
                                          />
                                      )}
                                      {midia.tipo === 'VIDEO' && !mediaErrors.includes(index) && (
                                          <video
                                              src={midia.url}
                                              controls
                                              className="rounded-3 w-100"
                                              onError={() => handleMediaError(index)}
                                          />
                                      )}
                                      {mediaErrors.includes(index) && (
                                          <p className="text-danger">Erro ao carregar mídia</p>
                                      )}
                                  </div>
                              </div>
                          </div>
                      ))
                : postimage && (
                      <div className="card-body d-block p-0 mb-3">
                          <div className="row ps-2 pe-2">
                              <div className="col-sm-12 p-1">
                                  <img src={`assets/images/${postimage}`} className="rounded-3 w-100" alt="post" />
                              </div>
                          </div>
                      </div>
                  )}

            {usarIdentidades && identidadeSelecionada && identidades!.length > 1 && (
                <div className="card-body p-0 mb-2">
                    <IdentidadeFeedSelector
                        identidades={identidades!}
                        identidadeSelecionada={identidadeSelecionada}
                        onSelecionar={setIdentidadeSelecionada}
                        compacto
                    />
                </div>
            )}

            <div className="card-body d-flex p-0">
                <div
                    id={`likeButton-${id}`}
                    className={`emoji-bttn d-flex align-items-center fw-600 text-grey-900 text-dark lh-26 font-xssss me-2 ${readOnly ? '' : 'pointer cursor-pointer'}`}
                    onClick={readOnly ? undefined : handleLike}
                    style={{ 
                        pointerEvents: readOnly || liking || unliking ? 'none' : undefined,
                        opacity: readOnly ? 0.5 : undefined
                    }}
                >
                    <i id={`likeIcon-${id}`} className={`bi bi-hand-thumbs-up me-1 btn-round-xs font-xss ${isActive ?  'bg-primary-gradiant text-white' : 'bg-white text-primary'}`}></i>
                    {likes} Curtidas
                </div>
                <span
                    className="d-flex align-items-center fw-600 text-grey-900 text-dark lh-26 font-xssss cursor-pointer"
                    onClick={() => setShowComments((v) => !v)}
                >
                    <i className="bi bi-chat text-dark text-grey-900 btn-round-sm font-lg"></i>
                    <span className="d-none-xss">{commentCount} Comentários</span>
                </span>
                <div
                    ref={shareButtonRef}
                    className={`pointer ms-auto d-flex align-items-center fw-600 text-grey-900 text-dark lh-26 font-xssss cursor-pointer ${menuClass}`}
                    id={`dropdownMenu${id}`}
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    onClick={toggleOpen}
                >
                    <i className="bi bi-share text-grey-900 text-dark btn-round-sm font-lg"></i>
                    <span className="d-none-xs">Compartilhar</span>
                </div>

                <div
                    ref={shareMenuRef}
                    id='shareMenu'
                    className={`dropdown-menu dropdown-menu-end p-4 rounded-xxl border-0 shadow-lg w-50 ${menuClass}`}
                    aria-labelledby={`dropdownMenu${id}`}
                >
                    <h4 className="fw-700 font-xss text-grey-900 d-flex align-items-center">
                        Compartilhar <i className="bi bi-x ms-auto font-xssss btn-round-xs bg-greylight text-grey-900 me-2" onClick={toggleOpen}></i>
                    </h4>
                    {/*
                      * Facebook e WhatsApp estão OCULTOS por decisão do owner (2026-08-31), não
                      * removidos — ver o débito "Compartilhamento em Facebook/WhatsApp sem prévia"
                      * no CLAUDE.md da raiz.
                      *
                      * Motivo: os dois montam a prévia buscando a URL pelo SERVIDOR deles e não
                      * executam JavaScript. Como o app é um SPA sem SSR e o `public/index.html`
                      * não tem NENHUMA meta tag Open Graph, eles recebem o mesmo HTML sem tags
                      * para todo post — o Facebook abre o compositor vazio e o WhatsApp mostra a
                      * URL crua. O X continua visível porque só cola a URL como texto e não
                      * precisa ler a página.
                      *
                      * Para restaurar: basta reexibir os dois <li>. O `ShareSocials` e as funções
                      * `shareOnFacebook`/`shareOnWhatsApp` foram mantidos intactos de propósito —
                      * eles estão corretos; o que falta é a página ter OG tags para eles lerem.
                      */}
                    <div className="card-body p-0 d-flex">
                        <ul className="d-flex align-items-center justify-content-between mt-2">
                            <li className="me-1"><span className="btn-round-lg pointer bg-twiiter" onClick={() => shareOnX(shareLink)}><i className="font-xs bi bi-twitter-x text-white"></i></span></li>
                        </ul>
                    </div>
                    <h4 className="fw-700 font-xssss mt-4 text-grey-500 d-flex align-items-center mb-3">Copiar Link</h4>
                    <div className='row'>

                        <div className="d-flex align-items-center">
                            <p
                                id='copyLinkInput'
                                className="bg-grey text-grey-500 font-xssss border-0 lh-32 p-2 font-xssss fw-600 rounded-3 w-100 theme-dark-bg mb-0"
                                style={{
                                    whiteSpace: 'nowrap',
                                    overflowX: 'auto',
                                    overflowY: 'hidden',
                                    display: 'block',
                                    scrollbarWidth: 'none', // Firefox
                                    msOverflowStyle: 'none', // IE/Edge
                                }}
                            >
                                {shareLink}
                            </p>
                            <style>
                                {`
                                    #copyLinkInput::-webkit-scrollbar {
                                        display: none;
                                    }
                                `}
                            </style>
                            <i
                                id='copyLinkIcon'
                                className="bi bi-copy ms-2 mt-1 font-xs text-grey-500 my-auto"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    const link = `${shareLink}`;
                                    navigator.clipboard.writeText(link).then(() => {
                                        setCopied(true);
                                        setTimeout(() => setCopied(false), 2000);
                                    });
                                }}
                            ></i>
                        </div>
                        
                    </div>
                    
                    {copied && (
                        <span className="text-primary font-xssss ms-2">Link copiado!</span>
                    )}
                </div>
            </div>
            {showComments && (
                <div className="mt-3 pt-3 border-top">
                    {!readOnly && canInteract && (
                        <div className="d-flex mb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Escreva um comentário..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                            />
                                <button
                                    className="btn btn-primary ms-2"
                                    disabled={!newComment.trim() || creatingComment}
                                    onClick={handleCreateComment}
                                >
                                    Comentar
                                </button>

                        </div>
                    )}
    
                    {commentsList
                        .slice()
                        .sort(
                            (a, b) =>
                                new Date(a.criadoEm).getTime() -
                                new Date(b.criadoEm).getTime()
                        )
                        .map((c) => (
                            <div key={c.id} className="d-flex mb-2 pb-2 border-bottom">
                                <figure className="avatar me-2">
                                    <img
                                        src={
                                            c.autorAtleta?.usuario.imagemUrl ||
                                            c.autorTorneio?.avatarUrl ||
                                            c.autorLiga?.imagemUrl ||
                                            '/assets/images/avatar.jpg'
                                        }
                                        alt="avatar"
                                        className="shadow-sm rounded-circle w35"
                                    />
                                </figure>
                                <div className="flex-grow-1">
                                    <div className="d-flex align-items-start">
                                        <div className="flex-grow-1">
                                            <h6 className="fw-700 text-grey-900 font-xssss mb-0">
                                                {c.autorAtleta?.usuario.nome || c.autorTorneio?.nome || c.autorLiga?.nome || ''} <span className="text-primary fw-light ps-2">{c.autorAtleta?.usuario?.username || c.autorTorneio?.username || c.autorLiga?.username || ''}</span>
                                                <span className="d-block font-xssss fw-500 text-grey-500">
                                                    {timeAgo(c.criadoEm)}
                                                </span>
                                            </h6>
                                        </div>
                                        {(((c.autorAtleta?.id ?? '') && (c.autorAtleta?.id === atletaId)) || ((c.autorTorneio?.id ?? '') && isAdmin)) && (
                                            <div
                                                className="ms-auto d-flex align-items-center"
                                                ref={(el) => {
                                                    if (el) {
                                                        commentMenuRefs.current.set(c.id, el);
                                                    } else {
                                                        commentMenuRefs.current.delete(c.id);
                                                    }
                                                }}
                                            >
                                                <i
                                                    className="bi bi-three-dots text-grey-900 btn-round-md font-xss pointer"
                                                    onClick={() => toggleCommentMenu(c.id)}
                                                    aria-expanded={openCommentMenuId === c.id}
                                                    aria-haspopup="true"
                                                />
                                                <div
                                                    className={`dropdown-menu dropdown-menu-end p-2 rounded-xxl border-0 shadow-lg ${openCommentMenuId === c.id ? 'show' : ''}`}
                                                    style={{ minWidth: 160 }}
                                                >
                                                    <button
                                                        className="dropdown-item text-danger"
                                                        disabled={deletingMap[c.id] === true}
                                                        onClick={() => handleDeleteComentario(c.id)}
                                                    >
                                                        {deletingMap[c.id] ? 'Deletando...' : 'Deletar Comentário'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <p className="mb-1 font-xssss text-grey-900">{c.texto}</p>
                                    <span
                                        className={`d-inline-flex align-items-center font-xssss text-grey-700 ${readOnly ? '' : 'pointer'}`}
                                        onClick={readOnly ? undefined : () => handleLikeComment(c.id)}
                                        style={{ 
                                            pointerEvents: readOnly ? 'none' : undefined,
                                            opacity: readOnly ? 0.5 : undefined
                                        }}
                                    >
                                        <i
                                            className={`bi bi-hand-thumbs-up me-1 btn-round-xs font-xss ${
                                                c.jaCurtiu
                                                    ? 'bg-primary-gradiant text-white'
                                                    : 'bg-white text-primary'
                                            }`}
                                        ></i>
                                        {c.curtidasCount ?? 0} Curtidas
                                    </span>
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

export default Postview;
